import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import {
    BehaviorSubject,
    distinct,
    filter,
    first,
    map,
    Observable,
    skip,
    skipWhile,
    Subject,
    take,
    takeUntil
} from 'rxjs';
import { LgvLoanApplicationService } from '../../../core/services/lgv-loan-application.service';
import { LgvLoanApp } from '../../../data-models/lgv-loan-application.model';
import { LgvFormlyModel } from '../../../data-models/formly.model';
import {
    FormType,
    LgvFormUtilityService
} from '../../../core/services/lgv-form-utility.service';
import {
    LgvClientService,
    LgvUserService,
    NotificationService
} from '../../../core/core.module';
import { LgvAuthMgrService } from '../../../core/services/lgv-auth-mgr.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { environment as env } from '../../../../environments/environment';
import { LoanAppPageService } from '../../../core/services/page-services/loan-app-page.service';
import { RespaTriggerDialogComponent } from '../../loan-app/respa-trigger-dialog/respa-trigger-dialog.component';
import { ExitWarningDialogComponent } from '../../loan-app/exit-warning-dialog/exit-warning-dialog.component';
import { IdleService } from '../../../core/services/idle-service.service';

@Component({
    selector: 'lgv-form-navigator',
    templateUrl: './form-navigator.component.html',
    styleUrls: ['./form-navigator.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormNavigatorComponent implements OnInit, OnDestroy, OnChanges {
    @Input() objectIdParam: string;
    @Input() level1Param = -1;
    @Input() level2Param = -1;
    @Input() defaultDataModel;
    @Input() formType;
    @Output() onNavigateFormLevel = new EventEmitter<any>();

    isLoading$ = new BehaviorSubject(true);
    isSubmitting$ = new BehaviorSubject(false);

    _savedLoanApp: LgvLoanApp.LoanApplicationDetail = null;
    savedLoanApp$ = new BehaviorSubject(this._savedLoanApp);

    _changeSummary: LgvFormlyModel.GroupSummary = null;
    changeSummary$ = new BehaviorSubject(this._changeSummary);

    onDestroy$ = new Subject();

    _formlyOptions: FormlyFormOptions = {};
    formlyOptions$ = new BehaviorSubject(this._formlyOptions);

    _pageMode: string = 'Default';
    pageMode$ = new BehaviorSubject(this._pageMode);

    form: any = new FormGroup({});
    // fields: FormlyFieldConfig[] = [];
    curLevel1 = -1;
    curLevel2 = -1;

    _fields: FormlyFieldConfig[] = [];
    fields$ = new BehaviorSubject(this._fields);

    _pctComplete: number = 0;
    pctComplete$ = new BehaviorSubject(this._pctComplete);

    _pctCompleteMsg: string = '';
    pctCompleteMsg$ = new BehaviorSubject(this._pctCompleteMsg);

    formSchema$ = new BehaviorSubject(null);
    formSchemaCopy: any;
    displayConfiguration$ = new BehaviorSubject(null);

    get isDev(): boolean {
        return env.envName === 'DEV';
    }

    get isFormComplete$(): Observable<boolean> {
        return this.fields$.pipe(
            filter(f => !!f),
            takeUntil(this.onDestroy$),
            map(fields => {
                if (!fields || !fields?.length) {
                    return false;
                }
                let topLevelSummaryList = this.buildSummaryList(fields);
                let numComplete = topLevelSummaryList.filter(
                    f => f?.sectionStatus === 'Complete'
                ).length;

                // terms is not part of any section, so check to make sure they are all complete
                let anyTermsMissing = false;
                let terms = this.lgvFormSvc.terms$.value;
                // 2nd term is not required
                for (let i = 0; i < terms.length; i++) {
                    let termValue = this.lgvFormSvc.getTermValue(terms[i].key);

                    if ((!termValue || termValue == 'No') && i != 1) {
                        anyTermsMissing = true;
                        break;
                    }
                }

                return (
                    topLevelSummaryList.length > 0 &&
                    topLevelSummaryList.length === numComplete &&
                    !anyTermsMissing
                );
            })
        );
    }

    get headerMsg(): string {
        return this.lgvFormSvc.isPreApplicationMode
            ? 'Review & Submit'
            : this.lgvFormSvc.isShortApp
            ? 'Let`s keep working on your loan application!'
            : 'Congrats on submitting your loan application!';
    }

    constructor(
        public lgvLoanAppSvc: LgvLoanApplicationService,
        public lgvFormSvc: LgvFormUtilityService,
        private notificationSvc: NotificationService,
        private matDialog: MatDialog,
        public lgvUserSvc: LgvUserService,
        private router: Router,
        public lgvAuthMgrSvc: LgvAuthMgrService,
        private lgvLoanAppPageSvc: LoanAppPageService,
        private idleService: IdleService,
        public lgvClientSvc: LgvClientService
    ) {}

    ngOnInit(): void {
        if (this.objectIdParam) {
            this.loadLoanApp(true);
        }

        this.lgvFormSvc.OnLoanSubmitted.pipe(
            takeUntil(this.onDestroy$)
        ).subscribe(x => {
            this.isSubmitting$.next(false);
            if (x === true) {
                this.onSuccessfulSubmisstion();
                this.lgvLoanAppPageSvc.loadLoanApp(this.objectIdParam);
            }
        });

        this.lgvFormSvc.OnGoToReviewAndSubmit.pipe(
            takeUntil(this.onDestroy$)
        ).subscribe(x => {
            setTimeout(() => {
                this.onReviewAndSubmit();
            }, 50);
        });

        this.lgvFormSvc.changeMap$
            .pipe(
                filter(f => !!f),
                takeUntil(this.onDestroy$)
            )
            .subscribe(x => {
                this.calcPctComlete();
            });

        // update form whenever dataModel$ is updated to reflect changes in UI
        this.lgvFormSvc.dataModel$
            .pipe(skip(1), takeUntil(this.onDestroy$))
            .subscribe(model => {
                this.form.patchValue(model);
            });

        this.lgvFormSvc.showRespaTrigger$
            .pipe(
                takeUntil(this.onDestroy$),
                skipWhile(x => !x),
                distinct()
            )
            .subscribe(() => {
                // show respa trigger only when app not submitted
                if (
                    !this.savedLoanApp$?.value?.loanInfo?.loanNumber &&
                    !this.lgvFormSvc.isSubmittedCanStillEditMode
                ) {
                    const dialogRef = this.matDialog.open(
                        RespaTriggerDialogComponent,
                        {
                            width: '500px',
                            disableClose: true
                        }
                    );
                    dialogRef
                        .afterClosed()
                        .pipe(first())
                        .subscribe(data => {
                            if (!!data?.action) {
                                const exitRef = this.matDialog.open(
                                    ExitWarningDialogComponent,
                                    {
                                        width: '500px'
                                    }
                                );
                                exitRef
                                    .afterClosed()
                                    .pipe(take(1))
                                    .subscribe(action => {
                                        if (action) {
                                            this.onSubmit();
                                        } else {
                                            this.idleService.startListener();
                                        }
                                    });
                            }
                        });
                }
            });

        this.idleService.userInactive$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(isInactive => {
                this.lgvFormSvc.saveForm(true);
                this.idleService.stopListening();
                this.navigateToCallingPage();
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.objectIdParam && !changes.objectIdParam.firstChange) {
            this.loadLoanApp(false);
        }

        if (changes.level1Param || changes.level2Param) {
            // potentially break out of review & submit mode
            this.pageMode$.next('Default');

            if (changes.level1Param) {
                this.curLevel1 = changes.level1Param.currentValue;
            }
            if (changes.level2Param) {
                this.curLevel2 = changes.level2Param.currentValue;
            }

            if (this.formSchemaCopy) {
                this.lgvFormSvc.setCurrentLevels(
                    this.curLevel1,
                    this.curLevel2
                );
                let isRoot = this.curLevel1 === -1 && this.curLevel2 === -1;
                this.fields$.next(
                    isRoot
                        ? this.lgvFormSvc.appNode$.value.appObj.fieldGroup
                        : [this.lgvFormSvc.appNode$.value.appObj]
                );

                // Always go to "Review & Submit mode" for "home" (-1,-1) if app has already been submitted
                if (
                    this.curLevel1 == -1 &&
                    this.curLevel2 == -1 &&
                    (this.lgvFormSvc.isSubmittedCanStillEditMode ||
                        this.lgvFormSvc.isReadOnlyMode)
                ) {
                    this.onReviewAndSubmit();
                }
            }

            this.calcPctComlete();
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    buildSummaryList(fieldGroupArray: any[]): LgvFormlyModel.GroupSummary[] {
        if (!fieldGroupArray || !fieldGroupArray.length) {
            return;
        }

        let topLevelSummaryList = [];
        let index = 0;
        for (let obj of fieldGroupArray) {
            let summary = this.lgvFormSvc.getChangeSummaryForLevel(index, -1);

            if (summary && !!!summary.isHidden) {
                topLevelSummaryList.push(summary);
            }

            index++;
        }
        return topLevelSummaryList;
    }

    calcPctComlete() {
        let fields = this.fields$.value;
        if (!fields || !fields.length) {
            return;
        }

        let topLevelSummaryList = this.buildSummaryList(fields);

        let numComplete = 0;
        if (topLevelSummaryList.length > 0) {
            numComplete = topLevelSummaryList.filter(
                f => f?.sectionStatus === 'Complete'
            ).length;
            this.pctComplete$.next(
                (numComplete / topLevelSummaryList.length) * 100
            );
            // console.log(`${numComplete} / ${topLevelSummaryList.length} = ${numComplete / topLevelSummaryList.length}`);
            this.pctCompleteMsg$.next(
                `Completed ${numComplete} of ${topLevelSummaryList.length} sections.`
            );
        } else {
            this.pctComplete$.next(0);
            this.pctCompleteMsg$.next(``);
        }
    }

    loadLoanApp(initialLoad: boolean) {
        this.isLoading$.next(true);

        this.lgvLoanAppSvc
            .fetchThisLoanApp(
                this.objectIdParam,
                this.lgvAuthMgrSvc?.ClientInfo?.id
            )
            .pipe(take(1))
            .subscribe(
                x => {
                    this.savedLoanApp$.next(x);

                    if (x.losType) {
                        const loanAppJson = this.lgvLoanAppSvc.getLoanAppSchemaFromLOS(
                            x.losType?.name
                        );
                        this.formSchema$.next(loanAppJson?.loanAppFields);
                        this.displayConfiguration$.next(
                            loanAppJson?.displayConfiguration
                        );
                        this.formSchemaCopy = structuredClone(
                            loanAppJson?.loanAppFields
                        );
                    }

                    if (this.defaultDataModel || this.formSchemaCopy) {
                        this.lgvFormSvc.init(
                            '',
                            this.defaultDataModel,
                            this.formSchemaCopy,
                            this.formType,
                            this.lgvAuthMgrSvc?.ClientInfo?.id
                        );
                        this.formlyOptions$.next({
                            formState: {
                                mainModel: this.lgvFormSvc.dataModel$.value
                            }
                        });
                        this.lgvFormSvc.setCurrentLevels(
                            this.curLevel1,
                            this.curLevel2
                        );

                        // kxk:todo - Move fields$ to service
                        let isRoot =
                            this.curLevel1 === -1 && this.curLevel2 === -1;
                        this.fields$.next(
                            isRoot
                                ? this.lgvFormSvc.appNode$.value.appObj
                                      .fieldGroup
                                : [this.lgvFormSvc.appNode$.value.appObj]
                        );
                    }
                    this.lgvFormSvc.updateFromDB(this.objectIdParam, x);
                    this.isLoading$.next(false);

                    if (
                        initialLoad &&
                        this.curLevel1 == -1 &&
                        this.curLevel2 == -1 &&
                        (this.lgvFormSvc.isSubmittedCanStillEditMode ||
                            this.lgvFormSvc.isReadOnlyMode)
                    ) {
                        this.onReviewAndSubmit();
                    }
                },
                err => {
                    console.log(err);
                    this.notificationSvc.error(
                        'Unable to load Loan Application'
                    );
                    this.isLoading$.next(false);
                }
            );
    }

    onSubmit() {
        this.lgvFormSvc.saveForm(true);
        this.isSubmitting$.next(true);
    }

    onReset() {
        if (this.formlyOptions$.value.resetModel) {
            this.formlyOptions$.value.resetModel();
        }
    }

    onModelChange(model: any) {
        this.lgvFormSvc.updateModel(model);
        let options = this.formlyOptions$.value;
        options.formState.mainModel = model;
        this.formlyOptions$.next(options);
    }

    onFormChange(e) {
        let fieldGroupArray = this.fields$.value;

        if (fieldGroupArray.length != 1) {
            return;
        }

        this.lgvFormSvc.updateChangeMap(fieldGroupArray[0].fieldGroup);

        //fire reloading of model in UI to reflect all changes
        this.form.patchValue(this.lgvFormSvc.dataModel$.value);
    }

    onReturn() {
        if (this.curLevel1 == -1) {
            return;
        }

        if (this.curLevel2 >= 0) {
            this.lgvFormSvc.navigate(this.curLevel1, -1);
        } else {
            this.lgvFormSvc.navigate(-1, -1);
        }
    }

    onReturnToCallingPage() {
        this.navigateToCallingPage();
    }

    navigateToCallingPage() {
        if (!this.lgvAuthMgrSvc.IsClient) {
            this.router.navigate([
                `/admin/loan-app/${this.lgvFormSvc.objectId}/details`
            ]);
        } else {
            console.log(
                'abc',
                this.lgvLoanAppPageSvc.selectedLoanApplication$.value
            );
            this.router.navigate([
                `/client/loan-app/${this.lgvFormSvc.objectId}/tasks`
            ]);
        }
    }

    onReviewAndSubmit() {
        this.pageMode$.next('ReviewAndSubmit');
    }
    onExitReviewAndSubmit() {
        this.pageMode$.next('Default');
    }

    objectDump(obj: any) {
        console.log('object dump:');
        console.log(obj);
    }

    onShowModel() {
        console.log(this.lgvFormSvc.dataModel$.value);
        this.notificationSvc.success('Model written to console');
    }

    onShowSchema() {
        console.log(this.lgvFormSvc.schema$.value);
        this.notificationSvc.success('Schema written to console');
    }

    onShowFormState() {
        let buildList = [];
        let keys = this.lgvFormSvc.changeMap$.value.keys();
        for (let k of keys) {
            buildList.push({
                key: k,
                value: this.lgvFormSvc.getChangeSummaryForKey(k)
            });
        }
        console.log(buildList);

        this.notificationSvc.success('State written to console');
    }

    onShowFormStateThisLevel() {
        let buildList = [];
        let keys = this.lgvFormSvc.getChangeSummaryKeysForThisLevel(
            this.lgvFormSvc.curLevel1$.value
        );
        for (let k of keys) {
            buildList.push({
                key: k,
                value: this.lgvFormSvc.getChangeSummaryForKey(k)
            });
        }
        console.log(buildList);
    }

    openViewTermsModal(index) {
        const term = this.lgvFormSvc.terms$.value[index];
        const viewTermsModal = this.matDialog.open(
            ConfirmationDialogComponent,
            {
                data: {
                    Confirmation: `<h1>${term?.terms?.title}</h1>${term?.terms?.detail}`,
                    woCancelButton: true
                }
            }
        );
    }

    onTermChanged(event: MatCheckboxChange, key: string) {
        this.lgvFormSvc.setTerm(key, event.checked);
        if (this.lgvFormSvc.isSubmittedCanStillEditMode) {
            this.lgvFormSvc.saveForm();
        }
    }

    onSuccessfulSubmisstion() {
        const dateTime = new Date().toLocaleString();
        const message =
            this.formType === FormType.FullLoanApp
                ? `Your Loan Application has been submitted as of ${dateTime}`
                : 'Thanks for submitting the loan application.';
        const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
            data: {
                Confirmation: message,
                woCancelButton: true
            }
        });
        dialogRef.afterClosed().subscribe(response => {
            if (response.action) {
                this.onReturnToCallingPage();
            }
        });
    }

    syncSummaryFields() {
        let changeSummary = this.lgvFormSvc.getChangeSummaryForCurrentLevel();
        if (
            !changeSummary ||
            changeSummary?.fieldList?.length == 0 ||
            changeSummary.refreshedInApp !== true
        ) {
            let fieldGroupArray = this.fields$.value;

            if (fieldGroupArray.length != 1) {
                return;
            }

            this.lgvFormSvc.updateChangeMap(fieldGroupArray[0].fieldGroup);
        }
    }
}
